import {
    Component,
    Object3D,
    InputComponent,
    PhysXComponent,
    RayHit,
    CollisionEventType,
} from '@wonderlandengine/api';
import { property } from '@wonderlandengine/api/decorators.js';
import { vec3 } from 'gl-matrix';
import { VEC3_DOWN } from '../utils/constants.js';
import { GlobalEvents } from '../core/global-events.js';
//import {Vignette} from './vignette.js';

/* Temporaries */

const _tempVecForward = vec3.create();
const _tempVecRight = vec3.create();
const _tempVecForce = vec3.create();

const _tempVecHeadPosition = vec3.create();
const _tempVecPlayerPosition = vec3.create();
const _tempVecEyeLeftPosition = vec3.create();
const _tempVecEyeRightPosition = vec3.create();
const _tempVecOffset = vec3.create();

/**
 * VR Locomotion component.
 * This component is used to move the player in VR based on Physx.
 *
 * @remarks
 * The component is dependent on an {@link InputComponent} component on the same object.
 */
export class Locomotion extends Component {
    static TypeName = 'locomotion';

    /**
     * The eye object is used to get the forward and right vectors of
     * where the player is looking.
     */
    @property.object({ required: true })
    eyeLeft!: Object3D;

    @property.object({ required: true })
    eyeRight!: Object3D;

    /**
     * The speed at which the player moves.
     */
    @property.float(12.0)
    speed = 12.0;

    @property.float(0.4)
    playerHeight!: number;
    @property.float(0.5)
    playerRadius!: number;
    /**
     * The deadzone threshold for the analog stick.
     */
    @property.float(0.2)
    deadzoneThreshold = 0.2;

    /**
     * The player physics object to apply the force to for movement.
     *
     * This is an Object3D containing an {@link PhysXComponent} component.
     */
    @property.object()
    playerPhysics?: Object3D;

    @property.object()
    playerHeadPhysicsObject?: Object3D;

    @property.object({ required: true })
    trackedSpace!: Object3D;

    public disableRoomscaleCorrection = false;
    public isRotating = false;
    private tmpPos = vec3.create();
    private tmpPosOld = vec3.create();
    private tmpVec = vec3.create();
    private tmpVec2 = vec3.create();
    private fwd = vec3.create();
    private lastSafePos: vec3 | undefined = undefined;
    private physxComponent?: PhysXComponent;
    private input?: InputComponent;
    private headForward: vec3 = [0, 0, 0];
    private rayhit: RayHit | undefined = undefined;
    touchingWalls: any;
    //private vignette: Vignette | null = null;

    start() {
        if (!this.eyeLeft) {
            throw new Error(
                `locomotion(${this.object.name}):eyeLeft is not defined`
            );
        }
        if (!this.playerPhysics) {
            throw new Error(
                'The locomotion component requires a reference to an object with a PhysxComponent.'
            );
        }

        const tempInput = this.object.getComponent(InputComponent);
        if (!tempInput) {
            throw new Error(
                'The locomotion component requires an input component on the same object.'
            );
        }
        this.input = tempInput;

        const tempPhysxComponent =
            this.playerPhysics.getComponent(PhysXComponent);
        if (!tempPhysxComponent) {
            throw new Error(
                'The locomotion component requires a physx component.'
            );
        }
        this.physxComponent = tempPhysxComponent;

        // const vignetteObject = this.eyeLeft.findByNameDirect('Vignette');
        // if (vignetteObject.length != 0) {
        //     this.vignette = vignetteObject[0].getComponent(Vignette);
        // } else {
        //     console.log('No vignette found in LeftEye children.');
        // }

        const physHead =
            this.playerHeadPhysicsObject?.getComponent(PhysXComponent)!;
        physHead.onCollision(this._onHeadCollision);
    }
    onActivate(): void {
        GlobalEvents.instance.teleportRequested.add(
            this._onTeleportRequested,
            this
        );
    }
    onDeactivate(): void {
        GlobalEvents.instance.teleportRequested.remove(
            this._onTeleportRequested
        );
    }

    update(dt: number) {
        if (!this.input?.xrInputSource?.gamepad?.axes?.length) {
            //  this.vignette?.stopMoving();
            return;
        }

        // if (this.isRotating) {
        //     return;
        // }

        const currentAxisX = this.input.xrInputSource.gamepad.axes[2];
        const currentAxisY = -this.input.xrInputSource.gamepad.axes[3];

        // remove deadzone to prevent drifting
        if (
            Math.abs(currentAxisX) < this.deadzoneThreshold &&
            Math.abs(currentAxisY) < this.deadzoneThreshold
        ) {
            // this.vignette?.stopMoving();

            if (this.disableRoomscaleCorrection) {
                return;
            }

            if (!this.physxComponent!.kinematic) {
                this.physxComponent!.kinematic = true;
            }

            //this.zeroCameraOffset();

            return;
        }

        this.headForward = [0, 0, 0];
        let direction: vec3 = [currentAxisX, 0, -currentAxisY];

        //direction[1] = 0; // Do not move on Y axis
        // If no direction is set, do not move
        // Move according to headObject Forward Direction
        // vec3.normalize(direction, direction);
        // direction[0] *= this.speed;
        // direction[2] *= this.speed;

        const playerPosition = this.playerPhysics!.getPositionWorld(
            this.tmpPos
        );
        // Combine direction with headObject
        vec3.transformQuat(
            direction,
            direction,
            this.eyeLeft.getTransformWorld()
        );

        let speed = this.speed;
        // Scale the fwd vector by the capsule radius
        const capsuleRadius = this.playerRadius;
        const rayStart = vec3.create();

        vec3.scaleAndAdd(rayStart, this.tmpPos, direction, -capsuleRadius);

        vec3.copy(this.tmpPosOld, this.tmpPos);
        const dist = dt * speed;

        // Move the tmpPos along the fwd direction
        vec3.scaleAndAdd(this.tmpPos, this.tmpPos, direction, dist);

        // check for objstacles 40cm above the ground
        rayStart[1] += 0.4;
        this.eyeLeft.getForwardWorld(_tempVecForward);
        /* Check for obstacles */
        // this.physxComponent?.onCollision()
        this.rayhit = this.engine.physics!.rayCast(
            rayStart,
            _tempVecForward,
            0b001 /* Blocked */,
            0.5
        );
        if (this.rayhit.hitCount > 0) {
            // If we hit something, we need to move back to the last safe position
            //  console.log(this.rayhit!.objects[0]!.name);
        }

        let ignoreCollision = false;
        const distanceToNavmesh = this.checkNavmesh(this.tmpPos);

        if (distanceToNavmesh !== undefined) {
            this.tmpPos[1] = distanceToNavmesh.locations[0][1];
            // If we are on the navmesh, we can move
            this.lastSafePos = vec3.clone(this.tmpPos);

            // Calculate final position:
            // tmpPos.y + (distance - 0.5) + desiredHeight
            // This adjusts for the 0.5 offset in the ray and sets the desired height
            //this.tmpPos[1] += distanceToNavmesh - 0.5 + 0.5;

            this.playerPhysics!.setPositionWorld(this.tmpPos);
        } else if (this.lastSafePos) {
            // No navmesh found - move to last safe position
            this.playerPhysics!.setPositionWorld(this.lastSafePos);
        } else {
            // No safe position either - use default height
            //  playerPosition[1] = -0.5;
            //this.object.setPositionWorld(playerPosition);
        }

        // this.physxComponent!.kinematic = false;

        // // Get forward and right vectors from the eye object
        // this.eyeLeft.getForwardWorld(_tempVecForward);
        // this.eyeLeft.getRightWorld(_tempVecRight);

        // // ignore the y axis
        // _tempVecForward[1] = 0;
        // _tempVecRight[1] = 0;

        // vec3.normalize(_tempVecForward, _tempVecForward);
        // vec3.normalize(_tempVecRight, _tempVecRight);

        // // Scale vectors by speed
        // vec3.scale(_tempVecForward, _tempVecForward, currentAxisY * this.speed);
        // vec3.scale(_tempVecRight, _tempVecRight, currentAxisX * this.speed);

        // // Add vectors to get final force

        // vec3.add(_tempVecForce, _tempVecForward, _tempVecRight);

        // // The Y axis of direction is set to zero and won't affect anything since
        // // we are moving only in X-Z plane.
        // _tempVecForce[1] = 0;

        // // Apply force to the player to move it.
        // this.physxComponent!.addForce(_tempVecForce);

        //this.vignette?.startMoving();
    }

    zeroCameraOffset() {
        this.playerPhysics!.getPositionWorld(_tempVecPlayerPosition);
        this.eyeLeft.getPositionWorld(_tempVecEyeLeftPosition);
        this.eyeRight.getPositionWorld(_tempVecEyeRightPosition);

        vec3.add(
            _tempVecHeadPosition,
            _tempVecEyeLeftPosition,
            _tempVecEyeRightPosition
        );
        vec3.scale(_tempVecHeadPosition, _tempVecHeadPosition, 0.5);

        _tempVecHeadPosition[1] = _tempVecPlayerPosition[1];
        this.trackedSpace.getPositionWorld(_tempVecPlayerPosition);

        vec3.subtract(
            _tempVecOffset,
            _tempVecHeadPosition,
            _tempVecPlayerPosition
        );

        this.playerPhysics!.setPositionWorld([
            _tempVecHeadPosition[0],
            _tempVecPlayerPosition[1],
            _tempVecHeadPosition[2],
        ]);

        // translate trackedSpace in oposite direction
        this.trackedSpace.setPositionWorld([
            _tempVecHeadPosition[0] - _tempVecOffset[0],
            _tempVecPlayerPosition[1],
            _tempVecHeadPosition[2] - _tempVecOffset[2],
        ]);
    }

    /**
     * Check if the target position is on the navmesh
     */
    checkNavmesh(targetPosition: vec3): RayHit | undefined {
        vec3.set(
            this.tmpVec2,
            targetPosition[0],
            targetPosition[1] + 0.5,
            targetPosition[2]
        );
        const rayhit = this.engine.physics?.rayCast(
            this.tmpVec2,
            VEC3_DOWN,
            0b0001,
            1
        )!;

        if (rayhit.hitCount > 0) {
            if (rayhit.objects[0]?.name === 'NavMesh') {
                //  this.lastSafePos = vec3.clone(targetPosition);
                return rayhit;
            }
        }
        return undefined;
    }

    private _onTeleportRequested = (position: vec3) => {
        this.playerPhysics!.setPositionWorld(position);
    };

    private _onHeadCollision = (
        type: CollisionEventType,
        other: PhysXComponent
    ) => {
        if (type === CollisionEventType.TriggerTouch) {
            if (this.lastSafePos) {
                // No navmesh found - move to last safe position
                this.playerPhysics!.setPositionWorld(this.lastSafePos);
            }
            //this.touchingWalls++;
            // console.log(`touch ${other.object.name}`); // Handle head trigger touch
        } else if (type === CollisionEventType.TriggerTouchLost) {
            //console.log(`touch lost ${other.object.name}`); // Handle head trigger touch lost
            //this.touchingWalls--;
        }
        // if (other.object.name === 'Obstacle') {
        //     // Handle head collision with obstacle
        //     console.log('Head collided with obstacle');
        // }
    };
}
